import { InlineIcon } from "@iconify/react";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "react-toastify";
import { NotificationBanner } from "../../../../../components/banners/NotificationBanner";
import { brand } from "../../../../../context";
import { useApoAuth } from "../../../../../hooks";
import { generatePDF } from "./api";

interface CustomerInfoModalProps {
  isDarkMode: boolean;
  sessionId: string;
  onClose: () => void;
}

export function CustomerInfoModal({ isDarkMode, sessionId, onClose }: CustomerInfoModalProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const { getIdTokenClaims } = useApoAuth();

  const generatePDFMutation = useMutation({
    mutationFn: async ({
      message,
      first_name,
      last_name,
      title,
      email,
      phone,
      company,
      address,
      country,
    }: {
      message?: string;
      first_name?: string;
      last_name?: string;
      title?: string;
      email?: string;
      phone?: string;
      company?: string;
      address?: string;
      country?: string;
    }) => {
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Authentication token not available");
      }
      const token = (tokenClaims as { __raw: string }).__raw;
      return generatePDF(
        {
          session_id: sessionId,
          message,
          first_name,
          last_name,
          title,
          email,
          phone,
          company,
          address,
          country,
        },
        token,
      );
    },
    onSuccess: (response) => {
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `quotation-summary-${sessionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully!");
      onClose();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || error?.message || "Failed to generate PDF";
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    // Extract all form fields
    const first_name = formData.get("first-name")?.toString() || undefined;
    const last_name = formData.get("last-name")?.toString() || undefined;
    const title = formData.get("title")?.toString() || undefined;
    const email = formData.get("email")?.toString() || undefined;
    const phone = formData.get("phone")?.toString() || undefined;
    const company = formData.get("company")?.toString() || undefined;
    const address = formData.get("address")?.toString() || undefined;
    const country = formData.get("country")?.toString() || undefined;
    const message = formData.get("message")?.toString() || undefined;

    generatePDFMutation.mutate({
      message,
      first_name,
      last_name,
      title,
      email,
      phone,
      company,
      address,
      country,
    });
  };

  return (
    <div className="Modal CustomerInfoModal" data-dark-mode={isDarkMode}>
      <div
        className={[
          "Modal__card",
          "CustomerInfoModal__card",
          "max-h-[80vh]",
          "grid",
          "grid-rows-[auto_1fr_auto]",
          isDarkMode ? "bg-gray-800" : "bg-white",
          "relative",
        ].join(" ")}
      >
        <div
          className={[
            "Modal__card__header",
            "CustomerInfoModal__card__header",
            "rounded-t",
            isDarkMode ? "bg-gray-700" : "bg-gray-100",
          ].join(" ")}
        >
          <h6
            className={`text-lg! font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
          >
            Customer Information
          </h6>
          <InlineIcon
            icon="ei:close"
            className={[
              "Modal__card__header__icon",
              isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900",
            ].join(" ")}
            onClick={onClose}
          />
        </div>
        <div
          className={[
            "Modal__card__body",
            "CustomerInfoModal__card__body",
            "rounded-b",
            "overflow-y-auto",
            isDarkMode ? "bg-gray-800" : "bg-white",
          ].join(" ")}
        >
          <div className="mx-4 mt-4">
            <NotificationBanner
              variant="info"
              title="Download Your Quotation Summary"
              description="Fill in your details below to generate a personalized PDF summary of your trip quotation. The PDF will include all trip details, destinations, transportation information, and day-by-day itinerary."
              showCloseButton={false}
            />
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First Name - Required */}
              <div>
                <label
                  htmlFor="first-name"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first-name"
                  name="first-name"
                  required
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Last Name - Required */}
              <div>
                <label
                  htmlFor="last-name"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last-name"
                  name="last-name"
                  required
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Title - Optional */}
              <div>
                <label
                  htmlFor="title"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Title
                </label>
                <select
                  id="title"
                  name="title"
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                >
                  <option value="">Select title</option>
                  <option value="Mr.">Mr.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Prof.">Prof.</option>
                </select>
              </div>

              {/* Email - Optional */}
              <div>
                <label
                  htmlFor="email"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Phone - Optional */}
              <div>
                <label
                  htmlFor="phone"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Company - Optional */}
              <div>
                <label
                  htmlFor="company"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Address - Optional */}
              <div className="md:col-span-2">
                <label
                  htmlFor="address"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Address
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows={3}
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    "resize-none",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Country - Optional */}
              <div>
                <label
                  htmlFor="country"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Country
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>

              {/* Message - Optional */}
              <div className="md:col-span-2">
                <label
                  htmlFor="message"
                  className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  className={[
                    "w-full",
                    "px-3",
                    "py-2",
                    "rounded-md",
                    "border",
                    "focus:outline-none",
                    "focus:ring-2",
                    "focus:ring-blue-500",
                    "resize-none",
                    isDarkMode
                      ? "bg-gray-800 border-gray-600 text-gray-100"
                      : "bg-white border-gray-300 text-gray-900",
                  ].join(" ")}
                />
              </div>
            </div>
          </form>
        </div>
        <div
          className={`Modal__card__actions grid grid-flow-col auto-cols-max justify-end gap-2 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <button
            type="button"
            className="CancelButton Button"
            data-brand={brand}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="Button"
            data-brand={brand}
            disabled={generatePDFMutation.isLoading}
            onClick={() => {
              formRef.current?.requestSubmit();
            }}
          >
            {generatePDFMutation.isLoading ? "Generating..." : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}
